import type { CellValueChangedEvent } from 'ag-grid-community';
import { Data, Match } from 'effect';
import { isNil } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useTheme } from 'styled-components';
import type { Portfolio } from 'venn-api';
import { useShouldUseNetAssetsInsteadOfAllocation } from '../../hooks/leverageAndShort';

import {
  proxySetIdState,
  blockAsOfDateSetting,
  blockCustomMetricSettingsState,
  type BlockId,
  blockLimitedRequestSubjects,
  cleanEmptyFromGrid,
  createPCBlockRowKey,
  pcBlock_customField_gridData,
  type StudioRequestSubject,
  subjectToKeyString,
} from 'venn-state';
import {
  AnalysisSubject,
  type AsOfDateSetting,
  assertNotNil,
  isHistoricalSubject,
  pruneNodesWithZeroAllocation,
  useHistoricalPortfolioSnapshot,
} from 'venn-utils';
import { useCustomMetadata } from '../../utils';
import { useGetRequestSubjectFee } from '../../blocks/utils/hooks/use-advisory-fee';
import { useAgGridStyle } from '../components/grid/AgGridThemeOverrides';
import {
  type AllocationRowData,
  buildCanonicalPathMapping,
  combinePortfolioRows,
  combineRowsWithCustomMetrics,
  makeColumnDefs,
  makePortfolioRows,
  makeTotalRow,
  MAX_CUSTOM_FIELD_CHARS,
  parsePcField,
  type PCField,
} from './usePortfolioComparisonUtils';
import { useSelectedMetrics } from './useSelectedMetrics';
import { usePageCustomMetricSymbol } from '../../blocks/components/core';
import { usePortfolioMultiFiltering } from '../../blocks/utils/hooks/use-portfolio-multi-filtering';
import { filterPortfolioTreeLevels, mergePortfolioTreeTopDown } from './multiLevelPortfolioFiltering';
import { PortfolioStrategyFilterType } from '../components/grid/types';

type SubjectType = Data.TaggedEnum<{
  ProForma: { subject: StudioRequestSubject };
  Historical: { subject: StudioRequestSubject; asOfDateSetting: AsOfDateSetting };
}>;

const SubjectType = Data.taggedEnum<SubjectType>();

const createSubject = ({
  subject,
  asOfDateSetting,
}: {
  subject?: StudioRequestSubject;
  asOfDateSetting: AsOfDateSetting;
}): SubjectType | undefined => {
  if (!subject) {
    return undefined;
  }
  if (isHistoricalSubject(subject)) {
    return SubjectType.Historical({
      subject,
      asOfDateSetting,
    });
  }
  return SubjectType.ProForma({ subject });
};

/**
 * Prepares a portfolio for comparison.
 * If portfolio is pro-forma - just returns it as is without modifications
 * If portfolio is historical - fetches snapshot with allocations as of selected date
 */
const usePortfolioForComparison = (subject?: SubjectType): Portfolio | undefined => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const snapshotParams: Parameters<typeof useHistoricalPortfolioSnapshot>[0] = Match.value(subject).pipe(
    Match.when(undefined, () => ({ asOfDateSetting: undefined, analysisSubject: undefined })),
    Match.tag('ProForma', ({ subject }) => ({
      asOfDateSetting: undefined,
      analysisSubject: subject.portfolio ? new AnalysisSubject(subject.portfolio, 'portfolio', proxySetId) : undefined,
    })),
    Match.tag('Historical', ({ subject, asOfDateSetting }) => ({
      asOfDateSetting,
      analysisSubject: subject.portfolio ? new AnalysisSubject(subject.portfolio, 'portfolio', proxySetId) : undefined,
    })),
    Match.exhaustive,
  );

  const {
    historicalPortfolioSnapshotResponse: { data: snapshot },
  } = useHistoricalPortfolioSnapshot({
    ...snapshotParams,
    options: {
      suspense: true,
    },
  });

  const prunedSnapshot = snapshot ? pruneNodesWithZeroAllocation(snapshot) : undefined;

  return Match.value(subject).pipe(
    Match.when(undefined, () => undefined),
    Match.tag('ProForma', ({ subject }) => subject.strategy ?? subject.portfolio),
    Match.tag('Historical', () => prunedSnapshot),
    Match.exhaustive,
  );
};

const checkSubjectAndPortfolioNotNil = (
  subjectAndPortfolio: readonly [StudioRequestSubject | undefined, Portfolio | undefined],
): subjectAndPortfolio is [StudioRequestSubject, Portfolio] => {
  const [subject, portfolio] = subjectAndPortfolio;
  return !isNil(subject) && !isNil(portfolio);
};

export const usePortfolioMultiFilteringInPortfolioComparison = (blockId: BlockId, subjects: StudioRequestSubject[]) => {
  const { hasMultiLevelPortfolioFiltering, filter } = usePortfolioMultiFiltering(blockId);

  if (!hasMultiLevelPortfolioFiltering || filter.filterType === PortfolioStrategyFilterType.DEFAULT) {
    return { subjects };
  }

  const includesInvestments = filter.levels.includes('investments');
  const numericLevels = filter.levels.filter((level): level is number => typeof level === 'number');
  const minLevel = numericLevels.length ? Math.min(...numericLevels) : -1;

  const shouldKeepLevel = (portfolio: Portfolio, path: number[]) => {
    const level = path.length;
    if (portfolio.fund) {
      return includesInvestments && level > minLevel;
    }

    return numericLevels.includes(level);
  };

  const flattenPortfolioLevel = (portfolio: Portfolio) => portfolio.children;

  const subjectsWithFilteredLevels = subjects.map((subject) =>
    subject.portfolio?.children.length
      ? ({
          ...subject,
          portfolio: {
            ...subject.portfolio,
            children: filterPortfolioTreeLevels(subject.portfolio.children, shouldKeepLevel, flattenPortfolioLevel),
          },
        } as StudioRequestSubject)
      : subject,
  );

  if (!filter.mergeRows) {
    return {
      subjects: subjectsWithFilteredLevels,
    };
  }

  const sum = (x?: number, y?: number) => (x ?? 0) + (y ?? 0);
  const keyFn = (portfolio: Portfolio) => portfolio.name;
  const mergePortfolios = (a: Portfolio, b: Portfolio): Portfolio => {
    return {
      ...a,
      absNetAssets: sum(a.absNetAssets, b.absNetAssets),
      allocation: sum(a.allocation, b.allocation),
      netAssets: sum(a.netAssets, b.netAssets),
      children: [...(a.children ?? []), ...(b.children ?? [])],
    };
  };

  const mergedSubjectsWithFilteredLevels = subjectsWithFilteredLevels.map((subject) =>
    subject.portfolio?.children.length
      ? ({
          ...subject,
          portfolio: {
            ...subject.portfolio,
            children: mergePortfolioTreeTopDown(subject.portfolio.children, keyFn, mergePortfolios),
          },
        } as StudioRequestSubject)
      : subject,
  );

  return {
    subjects: mergedSubjectsWithFilteredLevels,
  };
};

export const usePortfolioComparison = (blockId: BlockId) => {
  const agGridStyle = useAgGridStyle();
  const theme = useTheme();
  const getRequestSubjectFee = useGetRequestSubjectFee();
  let subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const blockAsOfDate = useRecoilValue(blockAsOfDateSetting(blockId));
  const selectedMetrics = useSelectedMetrics();
  const customMetricSymbol = usePageCustomMetricSymbol();
  const customMetrics = useRecoilValue(blockCustomMetricSettingsState(blockId));
  const customFieldData = useRecoilValue(pcBlock_customField_gridData(blockId));
  const customFieldsRef = useRef(customFieldData);
  customFieldsRef.current = customFieldData;

  const shouldUseNetAssetsInsteadOfAllocation = useShouldUseNetAssetsInsteadOfAllocation();
  const getAllocation = useCallback(
    (portfolio?: Portfolio) => {
      if (shouldUseNetAssetsInsteadOfAllocation) {
        return portfolio?.netAssets;
      }
      return portfolio?.allocation;
    },
    [shouldUseNetAssetsInsteadOfAllocation],
  );

  const { data: customMetadataMap = {} } = useCustomMetadata(subjects);

  const unfilteredPortfolio1 = subjects[0]?.portfolio;
  const unfilteredPortfolio2 = subjects[1]?.portfolio;
  const canonicalPathMapping1 = useMemo(() => buildCanonicalPathMapping(unfilteredPortfolio1), [unfilteredPortfolio1]);
  const canonicalPathMapping2 = useMemo(() => buildCanonicalPathMapping(unfilteredPortfolio2), [unfilteredPortfolio2]);

  const { subjects: subjectsWithFilteredPortfolios } = usePortfolioMultiFilteringInPortfolioComparison(
    blockId,
    subjects,
  );

  subjects = subjectsWithFilteredPortfolios;

  const { filter: multiLevelFilter, hasMultiLevelPortfolioFiltering } = usePortfolioMultiFiltering(blockId);

  const subject1 = subjects[0];
  const subject2 = subjects[1];

  const portfolioForComparison1 = usePortfolioForComparison(
    createSubject({ subject: subject1, asOfDateSetting: blockAsOfDate[0] }),
  );
  const portfolioForComparison2 = usePortfolioForComparison(
    createSubject({ subject: subject2, asOfDateSetting: blockAsOfDate[1] }),
  );
  const subjectPortfolioPairs = [
    [subject1, portfolioForComparison1] as const,
    [subject2, portfolioForComparison2] as const,
  ].filter(checkSubjectAndPortfolioNotNil);

  const onCellValueChanged = useRecoilCallback(
    ({ set }) =>
      (event: CellValueChangedEvent<AllocationRowData>) => {
        const { columnGroupKey, columnKey } = parsePcField(assertNotNil(event.colDef.field) as PCField);
        const rowKey = createPCBlockRowKey(assertNotNil(event.data.rowData[columnGroupKey]).canonicalRowIdPath);

        set(pcBlock_customField_gridData(blockId), (oldGrid) => {
          const newValue = ((event.newValue as unknown) ?? '').toString().substring(0, MAX_CUSTOM_FIELD_CHARS);
          const newRow = {
            ...oldGrid[rowKey],
            [columnKey]: newValue,
          };
          const newGrid = {
            ...oldGrid,
            [rowKey]: newRow,
          };
          return cleanEmptyFromGrid(newGrid);
        });
      },
    [blockId],
  );

  return useMemo(() => {
    const canonicalMappings = [canonicalPathMapping1, canonicalPathMapping2];
    const subjectRowData = subjectPortfolioPairs.map(([subject, portfolio], index) =>
      makePortfolioRows(
        portfolio,
        subjectToKeyString(subject),
        customMetadataMap,
        getAllocation,
        canonicalMappings[index] ?? new Map(),
      ),
    );
    const rowData = combineRowsWithCustomMetrics(
      combinePortfolioRows(subjects, subjectRowData),
      customMetrics,
      customFieldsRef.current,
    );

    const columnDefs = makeColumnDefs(
      selectedMetrics,
      subjects,
      agGridStyle,
      customMetricSymbol,
      theme,
      getRequestSubjectFee,
    );

    // Only show total row for "All Constituents" (DEFAULT), "All Strategies", "Top Level Strategies", or when top level (level 0) is included
    const shouldShowTotalRow =
      !hasMultiLevelPortfolioFiltering ||
      multiLevelFilter.filterType === PortfolioStrategyFilterType.DEFAULT ||
      multiLevelFilter.filterType === PortfolioStrategyFilterType.ALL_STRATEGIES ||
      multiLevelFilter.filterType === PortfolioStrategyFilterType.TOP_LEVEL_STRATEGIES ||
      (multiLevelFilter.filterType === PortfolioStrategyFilterType.CUSTOM_SELECTION &&
        multiLevelFilter.levels?.includes(0));

    const pinnedBottomRowData = shouldShowTotalRow
      ? makeTotalRow(subjectPortfolioPairs, customMetrics, customFieldsRef.current, rowData, getAllocation)
      : undefined;

    return {
      rowData,
      columnDefs,
      onCellValueChanged,
      pinnedBottomRowData,
    };
  }, [
    subjectPortfolioPairs,
    subjects,
    customMetrics,
    selectedMetrics,
    agGridStyle,
    customMetricSymbol,
    theme,
    onCellValueChanged,
    customMetadataMap,
    getAllocation,
    canonicalPathMapping1,
    canonicalPathMapping2,
    hasMultiLevelPortfolioFiltering,
    multiLevelFilter,
    getRequestSubjectFee,
  ]);
};
